import { Howl, Howler } from "howler";
import type { Material } from "@dyson/shared";

/**
 * Every sound in the game, synthesised at startup into small WAVs and played
 * through Howler. No audio files to ship; tweak the recipes below to change
 * how things sound.
 */

const RATE = 22050;

type Recipe = { seconds: number; loop?: boolean; volume: number; make: (t: number, n: () => number) => number };

/** Deterministic white noise in [-1, 1]. */
function noiseSource(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s / 2147483647) * 2 - 1;
  };
}

/** One-pole low-pass: smaller `k` = darker. Returns a stateful filter. */
function lowpass(k: number): (x: number) => number {
  let y = 0;
  return (x) => (y += k * (x - y));
}

const TAU = Math.PI * 2;

/** A few milliseconds' fade-in, so a sound doesn't start with a click. */
const ease = (t: number) => Math.min(1, t / 0.004);

/** A sine whose pitch can glide smoothly: it keeps its own phase, sample by sample. */
function osc(): (f: number) => number {
  let ph = 0;
  return (f) => Math.sin((ph = (ph + f / RATE) % 1) * TAU);
}

/** A train of tiny knocks, `rate` a second: wood sticking and slipping, which is what a creak is. */
function knocks(sharpness: number): (rate: number) => number {
  let ph = 0;
  return (rate) => Math.exp(-(ph = (ph + rate / RATE) % 1) * sharpness);
}

/** A one-pole low-pass whose brightness `k` can change as it goes. */
function sweepLowpass(): (x: number, k: number) => number {
  let y = 0;
  return (x, k) => (y += k * (x - y));
}

/** When each creak in the "creak" loop starts (s), how long it lasts, how fast it knocks and how it rings. Uneven, so the loop doesn't tick. */
const CREAKS = [
  { at: 0.4, length: 0.9, rate: 34, pitch: 310 },
  { at: 2.1, length: 0.5, rate: 52, pitch: 420 },
  { at: 3.3, length: 1.2, rate: 26, pitch: 240 },
  { at: 5.4, length: 0.6, rate: 44, pitch: 360 },
];

function recipes(): Record<string, Recipe> {
  // Filters are created per recipe so each sound has its own state.
  const lp = (k: number) => lowpass(k);
  const cannonLp = lp(0.08);
  const hitLp = lp(0.3);
  const digLp = lp(0.18);
  const softLp = lp(0.3);
  const placeLp = lp(0.1);
  const grassLp = lp(0.15);
  // High-passed noise: the bright crack of splintering wood.
  const crackLow = lp(0.2);
  const crackHp = (x: number) => x - crackLow(x);
  const stepLp = lp(0.04);
  const engineLp = lp(0.03);
  const launchLp = lp(0.15);
  const boardGritLow = lp(0.25);
  const boardHp = (x: number) => x - boardGritLow(x);
  const boardLp = lp(0.35);
  const boardThud = lp(0.05);
  const impactLp = lp(0.06);
  const impactCrackLow = lp(0.3);
  const impactCrack = (x: number) => x - impactCrackLow(x);
  const whaleTone = osc();
  const whaleOvertone = osc();
  const whaleLp = lp(0.02);
  const grappleLp = lp(0.4);
  const eatLp = lp(0.35);
  const flareRoar = sweepLowpass();
  const flareTone = osc();
  const doorKnock = knocks(10);
  const doorLp = lp(0.12);
  const creakKnock = knocks(7);
  const creakLp = lp(0.03);
  const humLp = lp(0.3);
  const lapLp = lp(0.04);
  const lapGurgleLow = lp(0.08);
  const lapGurgle = lp(0.3);
  const windLp = sweepLowpass();
  return {
    // Deep thump with a sweep down, plus a burst of smoke-rumble.
    cannon: {
      seconds: 0.9,
      volume: 0.7,
      make: (t, n) =>
        Math.sin(TAU * (50 + 60 * Math.exp(-t * 18)) * t) * Math.exp(-t * 5) * 0.8 +
        cannonLp(n()) * Math.exp(-t * 6) * 2.2,
    },
    // Splintering timber: crack plus a short noisy crunch.
    hit: {
      seconds: 0.4,
      volume: 0.8,
      make: (t, n) => Math.sin(TAU * 170 * t) * Math.exp(-t * 30) * 0.6 + hitLp(n()) * Math.exp(-t * 12) * 1.2,
    },
    // Digging, by what the block is made of.
    digRock: {
      // Gravelly scrape, chopped into grains.
      seconds: 0.2,
      volume: 0.45,
      make: (t, n) => digLp(n()) * Math.exp(-t * 18) * (0.6 + 0.4 * Math.sign(Math.sin(TAU * 60 * t))) * 1.6,
    },
    digSoft: {
      // A muffled rustle.
      seconds: 0.22,
      volume: 0.4,
      make: (t, n) => softLp(n()) * Math.exp(-t * 14) * (0.55 + 0.45 * Math.sin(TAU * 35 * t)) * 1.8,
    },
    digWood: {
      // A chop: a hollow knock and a splintering crack.
      seconds: 0.2,
      volume: 0.45,
      make: (t, n) => Math.sin(TAU * 210 * t) * Math.exp(-t * 35) * 0.7 + crackHp(n()) * Math.exp(-t * 55) * 0.9,
    },
    digMetal: {
      // A clang: a few clashing partials ringing out.
      seconds: 0.45,
      volume: 0.3,
      make: (t) => (Math.sin(TAU * 520 * t) + Math.sin(TAU * 1310 * t) * 0.5 + Math.sin(TAU * 2210 * t) * 0.3) * Math.exp(-t * 11) * 0.5,
    },
    digGlass: {
      // A crack and a tinkle.
      seconds: 0.3,
      volume: 0.3,
      make: (t, n) => (Math.sin(TAU * 2600 * t) + Math.sin(TAU * 3900 * t) * 0.5) * Math.exp(-t * 22) * 0.5 + n() * Math.exp(-t * 80) * 0.3,
    },
    // Placing.
    placeRock: {
      // A low thunk.
      seconds: 0.16,
      volume: 0.5,
      make: (t, n) => Math.sin(TAU * 95 * t) * Math.exp(-t * 28) + placeLp(n()) * Math.exp(-t * 40) * 0.8,
    },
    placeSoft: {
      seconds: 0.14,
      volume: 0.4,
      make: (t, n) => placeLp(n()) * Math.exp(-t * 25) * 1.6,
    },
    placeWood: {
      // Wooden knock.
      seconds: 0.14,
      volume: 0.5,
      make: (t, n) => Math.sin(TAU * 150 * t) * Math.exp(-t * 32) + n() * Math.exp(-t * 90) * 0.35,
    },
    placeMetal: {
      // A clank.
      seconds: 0.25,
      volume: 0.35,
      make: (t) => (Math.sin(TAU * 330 * t) + Math.sin(TAU * 830 * t) * 0.4) * Math.exp(-t * 20) * 0.8,
    },
    placeGlass: {
      seconds: 0.18,
      volume: 0.3,
      make: (t) => Math.sin(TAU * 1800 * t) * Math.exp(-t * 35),
    },
    // Footfalls, eased in so they don't click.
    stepRock: {
      // A soft, low thud with a little dark scuff.
      seconds: 0.12,
      volume: 0.12,
      make: (t, n) => ease(t) * (Math.sin(TAU * (75 + 40 * Math.exp(-t * 60)) * t) * Math.exp(-t * 40) * 0.9 + stepLp(n()) * Math.exp(-t * 30) * 1.2),
    },
    stepSoft: {
      // A quiet crunch of grass.
      seconds: 0.12,
      volume: 0.1,
      make: (t, n) => ease(t) * grassLp(n()) * Math.exp(-t * 28) * 1.6,
    },
    stepWood: {
      // A hollow knock on the deck.
      seconds: 0.12,
      volume: 0.12,
      make: (t, n) => ease(t) * (Math.sin(TAU * 165 * t) * Math.exp(-t * 45) + stepLp(n()) * Math.exp(-t * 60) * 0.5),
    },
    stepMetal: {
      // A dull clank on plate.
      seconds: 0.18,
      volume: 0.08,
      make: (t) => ease(t) * (Math.sin(TAU * 420 * t) * Math.exp(-t * 30) * 0.6 + Math.sin(TAU * 1050 * t) * Math.exp(-t * 45) * 0.25 + Math.sin(TAU * 80 * t) * Math.exp(-t * 40) * 0.6),
    },
    // Two-tone latch for taking or leaving the helm.
    helm: {
      seconds: 0.16,
      volume: 0.35,
      make: (t) => Math.sin(TAU * (t < 0.06 ? 880 : 660) * t) * Math.exp(-((t % 0.06) * 50)),
    },
    // Double-jump launch: a rising whoosh of filtered noise.
    launch: {
      seconds: 0.5,
      volume: 0.5,
      make: (t, n) => launchLp(n()) * Math.min(1, t * 30) * Math.exp(-t * 6) * 3 * (1 + t * 2),
    },
    select: {
      seconds: 0.05,
      volume: 0.2,
      make: (t) => Math.sin(TAU * 1250 * t) * Math.exp(-t * 90),
    },
    // Something heavy coming aboard: a grinding scrape along the hull, then a thud.
    board: {
      seconds: 0.95,
      volume: 0.8,
      make: (t, n) => {
        const scrape = boardLp(boardHp(n())) * Math.min(1, t * 40) * Math.max(0, 1 - t / 0.42) * (0.6 + 0.4 * Math.sin(TAU * 23 * t + Math.sin(TAU * 7 * t) * 3)) * 3.5;
        const s = t - 0.38;
        const thud = s < 0 ? 0 : ease(s) * (Math.sin(TAU * (45 + 55 * Math.exp(-s * 25)) * s) * Math.exp(-s * 7) + boardThud(n()) * Math.exp(-s * 10) * 2.5);
        return scrape + thud;
      },
    },
    // A ship crash: a deep sweeping boom under a long crunch of breaking hull (deeper and longer than "hit").
    impact: {
      seconds: 1.5,
      volume: 0.9,
      make: (t, n) => {
        // The crunch comes in grains, slowing as the wreckage settles.
        const grains = 0.5 + 0.5 * Math.sign(Math.sin(TAU * (38 - 12 * t) * t + Math.sin(TAU * 11 * t) * 2));
        return (
          Math.sin(TAU * (32 + 50 * Math.exp(-t * 10)) * t) * Math.exp(-t * 3) * 0.9 +
          impactLp(n()) * Math.exp(-t * 3.5) * grains * 2 +
          impactCrack(n()) * Math.exp(-t * 20) * 0.8
        );
      },
    },
    // Lub-dub: two soft low thumps, the second weaker.
    heartbeat: {
      seconds: 0.5,
      volume: 0.55,
      make: (t) => {
        const thump = (s: number) => (s < 0 ? 0 : ease(s) * Math.sin(TAU * (48 + 25 * Math.exp(-s * 40)) * s) * Math.exp(-s * 20));
        return thump(t) + thump(t - 0.17) * 0.7;
      },
    },
    // A space whale's long low moan: a slow glide up and back down, with a breathy edge.
    whale: {
      seconds: 3.6,
      volume: 0.7,
      make: (t, n) => {
        const f = 62 + 40 * Math.sin(Math.PI * Math.min(1, t / 2.6)) - 14 * Math.max(0, t - 2.6) + Math.sin(TAU * 5 * t) * 1.5;
        const swell = Math.sin(Math.PI * (t / 3.6)) ** 1.5;
        return swell * (whaleTone(f) * 0.7 + whaleOvertone(f * 2.01) * 0.25 + whaleLp(n()) * 1.2);
      },
    },
    // A grapple line: a quick zip of rope, then the twang as it goes taut.
    grapple: {
      seconds: 0.45,
      volume: 0.45,
      make: (t, n) => {
        const x = n();
        const zip = (x - grappleLp(x)) * Math.max(0, 1 - t / 0.14) * (0.5 + 0.5 * Math.sin(TAU * 120 * t)) * 0.6;
        const s = t - 0.1;
        const f = 190 * (1 + 0.15 * Math.exp(-s * 30));
        const twang = s < 0 ? 0 : ease(s) * (Math.sin(TAU * f * s) + Math.sin(TAU * f * 2 * s) * 0.4 + Math.sin(TAU * f * 3 * s) * 0.2) * Math.exp(-s * 9) * 0.6;
        return zip + twang;
      },
    },
    // A soft blip played backwards: it swells in and stops, falling a little in pitch.
    undo: {
      seconds: 0.14,
      volume: 0.2,
      make: (t) => Math.sin(TAU * (700 + 300 * (1 - t / 0.14)) * t) * (t / 0.14) ** 2,
    },
    // Three crunchy bites, each a little softer.
    eat: {
      seconds: 0.39,
      volume: 0.35,
      make: (t, n) => {
        const s = t % 0.13;
        const bite = Math.floor(t / 0.13);
        return ease(s) * eatLp(n()) * Math.exp(-s * 35) * (0.55 + 0.45 * Math.sign(Math.sin(TAU * 90 * t))) * (2.2 - bite * 0.4);
      },
    },
    // A sun flaring: a low roar that rises and brightens, then dies away.
    flare: {
      seconds: 3.2,
      volume: 0.7,
      make: (t, n) => {
        const rise = Math.min(1, t / 2.4);
        const swell = rise ** 2 * Math.min(1, (3.2 - t) / 0.7);
        return swell * (flareRoar(n(), 0.02 + 0.1 * rise) * 2.4 + flareTone(38 + 50 * rise) * 0.3);
      },
    },
    // A door: the hinge creaks, then it clunks home.
    door: {
      seconds: 0.62,
      volume: 0.45,
      make: (t, n) => {
        const creak = t < 0.36 ? Math.sin(Math.PI * (t / 0.36)) * doorKnock(40 + 20 * Math.sin(TAU * 3 * t)) * Math.sin(TAU * 620 * t) * 0.5 : 0;
        const s = t - 0.4;
        const clunk = s < 0 ? 0 : ease(s) * (Math.sin(TAU * 125 * s) * Math.exp(-s * 30) + doorLp(n()) * Math.exp(-s * 40) * 1.5);
        return creak + clunk;
      },
    },
    // Furnace rumble: low hum and dark roar, looped while you fly.
    engine: {
      seconds: 2,
      loop: true,
      volume: 1,
      make: (t, n) => Math.sin(TAU * 46 * t) * 0.35 + Math.sin(TAU * 92 * t) * 0.12 + engineLp(n()) * 3.5,
    },
    // Timbers working against each other: a few slow creaks at uneven times, over a faint dark groan.
    creak: {
      seconds: 7.1,
      loop: true,
      volume: 1,
      make: (t, n) => {
        let out = creakLp(n());
        for (const c of CREAKS) {
          const s = t - c.at;
          if (s < 0 || s > c.length) continue;
          // Each creak slows as it goes, the way a joint does as it takes the strain.
          out += Math.sin(Math.PI * (s / c.length)) * creakKnock(c.rate * (1 - 0.35 * (s / c.length))) * Math.sin(TAU * c.pitch * s) * 0.8;
        }
        return out;
      },
    },
    // Fission hum: mains-like harmonics, two of them a hair apart so it beats slowly, and a faint crackle.
    hum: {
      seconds: 2.1,
      loop: true,
      volume: 1,
      make: (t, n) => {
        const x = n();
        return Math.sin(TAU * 55 * t) * 0.3 + Math.sin(TAU * 55.5 * t) * 0.2 + Math.sin(TAU * 110 * t) * 0.15 + Math.sin(TAU * 165 * t) * 0.07 + (x - humLp(x)) * 0.03;
      },
    },
    // Water lapping: dark swells of noise rising and falling unevenly, with a little gurgle on each.
    lap: {
      seconds: 5.1,
      loop: true,
      volume: 1,
      make: (t, n) => {
        const swell = Math.max(0, Math.sin(TAU * 0.6 * t + Math.sin(TAU * 0.2 * t) * 2)) ** 2;
        const x = n();
        const gurgle = lapGurgle(x - lapGurgleLow(x)) * (0.5 + 0.5 * Math.sin(TAU * 9 * t + Math.sin(TAU * 1.4 * t) * 4));
        return lapLp(x) * (0.3 + 0.7 * swell) * 3 + gurgle * swell * 0.5;
      },
    },
    // Soft wind: noise that gusts louder and brighter, then falls back.
    wind: {
      seconds: 8.1,
      loop: true,
      volume: 1,
      make: (t, n) => {
        const gust = 0.5 + 0.3 * Math.sin(TAU * (t / 8) + Math.sin(TAU * (t / 4)) * 1.2) + 0.2 * Math.sin(TAU * (t / 2.7));
        return windLp(n(), 0.01 + 0.04 * gust) * (0.4 + gust) * 2.4;
      },
    },
  };
}

function render(r: Recipe, seed: number): Float32Array {
  const n = noiseSource(seed);
  const len = Math.floor(r.seconds * RATE);
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) out[i] = r.make(i / RATE, n);
  // Loops: cross-fade the ends so the seam is silent. One-shots: fade out the tail to avoid a click.
  const fade = Math.floor(RATE * (r.loop ? 0.1 : 0.01));
  for (let i = 0; i < fade; i++) {
    const k = i / fade;
    if (r.loop) out[i] = out[i]! * k + out[len - fade + i]! * (1 - k);
    else out[len - 1 - i] = out[len - 1 - i]! * k;
  }
  return r.loop ? out.subarray(0, len - fade) : out;
}

/** 16-bit mono PCM WAV as a data URI. */
function toWav(samples: Float32Array): string {
  const bytes = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const str = (o: number, s: string) => [...s].forEach((c, i) => bytes.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF");
  bytes.setUint32(4, 36 + samples.length * 2, true);
  str(8, "WAVEfmt ");
  bytes.setUint32(16, 16, true);
  bytes.setUint16(20, 1, true);
  bytes.setUint16(22, 1, true);
  bytes.setUint32(24, RATE, true);
  bytes.setUint32(28, RATE * 2, true);
  bytes.setUint16(32, 2, true);
  bytes.setUint16(34, 16, true);
  str(36, "data");
  bytes.setUint32(40, samples.length * 2, true);
  samples.forEach((v, i) => bytes.setInt16(44 + i * 2, Math.max(-1, Math.min(1, v)) * 0x7fff, true));
  let bin = "";
  const u8 = new Uint8Array(bytes.buffer);
  for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode(...u8.subarray(i, i + 0x8000));
  return `data:audio/wav;base64,${btoa(bin)}`;
}

export type SoundName =
  | "cannon"
  | "hit"
  | `dig${Capitalize<Material>}`
  | `place${Capitalize<Material>}`
  | `step${Capitalize<Material>}`
  | "helm"
  | "select"
  | "launch"
  | "board"
  | "impact"
  | "heartbeat"
  | "whale"
  | "grapple"
  | "undo"
  | "eat"
  | "flare"
  | "door";
export type LoopName = "engine" | "creak" | "hum" | "lap" | "wind";

/** Where a sound is relative to the listener: 0..1 loudness and -1..1 stereo pan. */
export interface Placement {
  gain: number;
  pan: number;
}

export class Sfx {
  private readonly howls = new Map<string, Howl>();
  private readonly volumes = new Map<string, number>();
  private readonly loopIds = new Map<LoopName, number>();
  private readonly loopLevel = new Map<LoopName, number>();
  /** The volume each playing loop was last set to. */
  private readonly loopApplied = new Map<LoopName, number>();
  private isMuted = false;
  /** The sound-effects and music sliders (0..1); the master one goes straight to Howler. */
  private sfxVolume = 1;
  private music = 1;

  constructor() {
    let seed = 1;
    for (const [name, r] of Object.entries(recipes())) {
      this.howls.set(name, new Howl({ src: [toWav(render(r, seed++ * 7919))], format: ["wav"], loop: !!r.loop }));
      this.volumes.set(name, r.volume);
    }
  }

  /** Play a one-shot. `rate` shifts pitch (and speed) for variety; `volume` scales it (e.g. by how hard a crash was). */
  play(name: SoundName, at: Placement = { gain: 1, pan: 0 }, rate = 1, volume = 1): void {
    const howl = this.howls.get(name);
    const gain = at.gain * volume * this.sfxVolume;
    if (!howl || gain <= 0.01) return;
    const id = howl.play();
    howl.volume(Math.min(1, gain * (this.volumes.get(name) ?? 1)), id);
    howl.stereo(Math.max(-1, Math.min(1, at.pan)), id);
    howl.rate(rate, id);
  }

  /** Set a looping sound's level (0 stops it), easing toward it each frame. */
  loop(name: LoopName, level: number, dt: number): void {
    const howl = this.howls.get(name);
    if (!howl) return;
    const current = this.loopLevel.get(name) ?? 0;
    const next = current + (level - current) * (1 - Math.exp(-dt * 8));
    this.loopLevel.set(name, next);
    let id = this.loopIds.get(name);
    if (next > 0.005 && id === undefined) {
      id = howl.play();
      this.loopIds.set(name, id);
    }
    if (id === undefined) return;
    if (next <= 0.005 && level <= 0.005) {
      howl.stop(id);
      this.loopIds.delete(name);
      this.loopApplied.delete(name);
      return;
    }
    // Only when it's changed enough to hear: each call is another automation event on the audio thread.
    const volume = Math.min(1, next * this.sfxVolume);
    if (Math.abs(volume - (this.loopApplied.get(name) ?? -1)) < 0.004) return;
    this.loopApplied.set(name, volume);
    howl.volume(volume, id);
  }

  /** The volume sliders, 0..1 each. Loops pick up the new sound-effects level on their next frame. */
  setVolumes(v: { master: number; sfx: number; music: number }): void {
    const clamp = (x: number) => Math.max(0, Math.min(1, x));
    Howler.volume(clamp(v.master));
    this.sfxVolume = clamp(v.sfx);
    this.music = clamp(v.music);
  }

  /** The music slider (0..1), for Music to follow. */
  get musicVolume(): number {
    return this.music;
  }

  get muted(): boolean {
    return this.isMuted;
  }

  toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    Howler.mute(this.isMuted);
    return this.isMuted;
  }
}
